import type { Subscription } from '../../../../../types';

import ACCOUNT_ROUTES from '../../../../../constants/account-routes';
import { ConfirmationDialog } from '../../dialogs/confirmation';
import { EventScopeProvider } from '@blueshift-ui/analytics';
import Snackbar from '@blueshift-ui/feedback/dist/components/snackbar';
import { add } from 'date-fns';
import logHandledError from '@blueshift-ui/telemetry/dist/helpers/log-handled-error';
import useItem from '../../../../../hooks/use-item';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';
import useUpdateSubscription from '../../../../../hooks/subscription/use-update-subscription';

const PAYGO_ITEM_ID =
  process.env.VITE_MODE !== 'production'
    ? '3825d620-029c-481e-ba33-35b897908069'
    : 'e6909fd8-0bae-49a8-83d8-a2b642014e2d'; // TODO: find a better long-term solution - KZN-1281

interface PayAsYouGoConfirmationDialogProps {
  onCancel: () => void;
  onClose: () => void;
  openConfirmationDialog: boolean;
  subscription: Subscription;
}

function PayAsYouGoConfirmationDialog({
  onCancel,
  onClose,
  openConfirmationDialog,
  subscription,
}: PayAsYouGoConfirmationDialogProps) {
  const navigate = useNavigate();
  const { translate } = useTranslation('change_plan.pay_as_you_go');
  const { makeRequestUpdateSubscription } = useUpdateSubscription({
    currentSubscription: subscription,
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { data: item } = useItem({
    itemId: PAYGO_ITEM_ID,
  });

  async function handleUpdateSubscription() {
    try {
      await makeRequestUpdateSubscription({
        parameters: {
          new_item_id: item?.id,
          new_purchase_price_cents: item?.price_cents,
          old_item_id: subscription.item_id,
          old_purchase_price_cents: subscription.purchase_price_cents,
        },
        scheduled_at: add(new Date(subscription.next_billing_date), {
          hours: -2,
        }).toISOString(),
        transformation_type: 'membership_change_requested',
      });

      navigate(`/${ACCOUNT_ROUTES.membership}`);
    } catch (err: any) {
      logHandledError(err.message, {
        currentSubscriptionId: subscription,
        desiredSubscriptionId: item?.id,
      });

      setSnackbarOpen(true);
    }
  }

  return (
    <EventScopeProvider eventScope={{ pageSection: 'pay_as_you_go_confirmation_dialog' }}>
      <ConfirmationDialog
        children={translate('message')}
        onAsyncConfirm={handleUpdateSubscription}
        onCancel={onCancel}
        onClose={onClose}
        open={openConfirmationDialog}
        title={translate('title')}
      />

      <Snackbar
        message={translate('error')}
        onClose={() => setSnackbarOpen(false)}
        open={snackbarOpen}
        severity="error"
      />
    </EventScopeProvider>
  );
}

export default PayAsYouGoConfirmationDialog;
